import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';
import { LivenessSession } from '@shared/models/face-liveness.model';
import { getDeviceName } from '@shared/utils/device-name.util';
import {
  FaceEnrollmentResult,
  ForgotPasswordRequest,
  ForgotPasswordResult,
  LoginCredentials,
  LoginResponse,
  LoginResult,
  RefreshTokenResult,
  ResetPasswordRequest,
  ResetPasswordResult,
} from '../models/auth.model';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly http = inject(HttpClient);

  /** Full URLs built from the environment base URL (which ends with a trailing slash). */
  private readonly epLogin = `${environment.apiBaseUrl}login`;
  private readonly epRefresh = `${environment.apiBaseUrl}auth/refresh`;
  private readonly epLogout = `${environment.apiBaseUrl}logout`;
  private readonly epForgotPassword = `${environment.apiBaseUrl}auth/forgot-password`;
  private readonly epResetPassword = `${environment.apiBaseUrl}auth/reset-password`;
  private readonly epFaceEnrollmentSession = `${environment.apiBaseUrl}auth/mfa/face/enroll/liveness-session`;
  private readonly epFaceEnrollment = `${environment.apiBaseUrl}auth/mfa/face/enroll`;
  private readonly epLoginFaceSession = `${environment.apiBaseUrl}auth/mfa/face/liveness-session`;
  private readonly epLoginFace = `${environment.apiBaseUrl}auth/mfa/face`;
  private readonly epFaceLoginSession = `${environment.apiBaseUrl}auth/face-login/liveness-session`;
  private readonly epFaceLogin = `${environment.apiBaseUrl}auth/face-login`;

  /** Authenticates the user against the API and returns the token and user. */
  login(credentials: LoginCredentials): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.epLogin, credentials);
  }

  /** Verifies that the given email belongs to an existing account. */
  forgotPassword(payload: ForgotPasswordRequest): Observable<ForgotPasswordResult> {
    return this.http.post<ForgotPasswordResult>(this.epForgotPassword, payload);
  }

  /** Sets a new password for the account identified by the given CPF. */
  resetPassword(payload: ResetPasswordRequest): Observable<ResetPasswordResult> {
    return this.http.post<ResetPasswordResult>(this.epResetPassword, payload);
  }

  /** Opens a liveness session for enrolling the signed-in user's face. The interceptor attaches the bearer token. */
  openFaceEnrollmentSession(): Observable<LivenessSession> {
    return this.http.post<LivenessSession>(this.epFaceEnrollmentSession, {});
  }

  /** Enrolls the face from a finished liveness session. */
  enrollFace(livenessSessionId: string): Observable<FaceEnrollmentResult> {
    return this.http.post<FaceEnrollmentResult>(this.epFaceEnrollment, {
      liveness_session_id: livenessSessionId,
    });
  }

  /**
   * Opens a liveness session for the login's second step. There is no session
   * yet: the challenge token from the password step is the credential, and the
   * route is public (`isPublicAuthUrl`), so no bearer goes with it.
   */
  openLoginFaceSession(challengeToken: string): Observable<LivenessSession> {
    return this.http.post<LivenessSession>(this.epLoginFaceSession, {
      challenge_token: challengeToken,
    });
  }

  /** Answers the login challenge with a finished liveness session; returns the session. */
  verifyLoginFace(challengeToken: string, livenessSessionId: string): Observable<LoginResult> {
    return this.http.post<LoginResult>(this.epLoginFace, {
      challenge_token: challengeToken,
      liveness_session_id: livenessSessionId,
      device_name: getDeviceName(),
    });
  }

  /**
   * Opens a liveness session for the direct face login (email + face, no
   * password). Public like the 2FA routes; the API opens it for any email on
   * purpose, so an unknown one is only refused when the session is answered.
   */
  openFaceLoginSession(email: string): Observable<LivenessSession> {
    return this.http.post<LivenessSession>(this.epFaceLoginSession, { email });
  }

  /** Signs in with the email and a finished liveness session. The email must match the session's. */
  faceLogin(email: string, livenessSessionId: string): Observable<LoginResult> {
    return this.http.post<LoginResult>(this.epFaceLogin, {
      email,
      liveness_session_id: livenessSessionId,
      device_name: getDeviceName(),
    });
  }

  /** Rotates the access token. The interceptor attaches the current bearer token. */
  refresh(): Observable<RefreshTokenResult> {
    return this.http.post<RefreshTokenResult>(this.epRefresh, {});
  }

  /** Revokes the current session on the API. The interceptor attaches the bearer token. */
  logout(): Observable<void> {
    return this.http.post<void>(this.epLogout, {});
  }
}
